// Phase 4 Part 2 — ownership, library, secure downloads, access codes, and
// admin commerce controls. Every read is RLS-scoped to the caller; privileged
// writes load the admin client inside the handler after verifying the caller.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { CatalogRow, OwnedItem } from "@/lib/ownership";
import { normalizeProductType, resolvePrice } from "@/lib/ownership";
import {
  CATALOG_COLUMNS,
  auditEntitlement,
  grantEntitlements,
  loadMembership,
  loadOwnership,
} from "@/lib/ownership.server";
import { checkRateLimit } from "@/lib/rate-limit.server";

const EnvSchema = z.enum(["sandbox", "live"]);
const SlugSchema = z.string().trim().min(1).max(200).regex(/^[a-z0-9][a-z0-9-_]*$/i);

export type LibraryOrder = {
  id: string;
  orderNumber: string | null;
  productSlug: string;
  productTitle: string;
  amountCents: number;
  currency: string;
  status: string;
  fulfillmentStatus: string | null;
  createdAt: string;
  refundedAt: string | null;
  receiptUrl: string | null;
};

export type LibraryPayload = {
  items: OwnedItem[];
  membership: { active: boolean; tier: string | null };
  orders: LibraryOrder[];
  downloads: { productSlug: string; assetLabel: string | null; createdAt: string }[];
};

/** Canonical ownership snapshot — the single source of truth for all clients. */
export const getMyOwnership = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ environment: EnvSchema.optional() }).parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const { items, bySlug, membership } = await loadOwnership(
      context.supabase,
      context.userId,
      data.environment,
    );
    return { items, bySlug, membership };
  });

/** Unified owned-content library + order history. */
export const getMyLibrary = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ environment: EnvSchema.optional() }).parse(input ?? {}))
  .handler(async ({ data, context }): Promise<LibraryPayload> => {
    const { supabase, userId } = context;
    const [ownership, ordersRes, downloadsRes] = await Promise.all([
      loadOwnership(supabase, userId, data.environment),
      supabase
        .from("orders")
        .select(
          "id, order_number, product_slug, product_title, amount_cents, currency, status, fulfillment_status, created_at, refunded_at",
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(100),
      supabase
        .from("download_events")
        .select("product_slug, asset_label, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50),
    ]);

    return {
      items: ownership.items,
      membership: ownership.membership,
      orders: ((ordersRes.data ?? []) as any[]).map((row) => ({
        id: row.id,
        orderNumber: row.order_number ?? null,
        productSlug: row.product_slug,
        productTitle: row.product_title ?? row.product_slug,
        amountCents: row.amount_cents ?? 0,
        currency: row.currency ?? "usd",
        status: row.status ?? "pending",
        fulfillmentStatus: row.fulfillment_status ?? null,
        createdAt: row.created_at,
        refundedAt: row.refunded_at ?? null,
        receiptUrl: null,
      })),
      downloads: ((downloadsRes.data ?? []) as any[]).map((row) => ({
        productSlug: row.product_slug,
        assetLabel: row.asset_label ?? null,
        createdAt: row.created_at,
      })),
    };
  });

/** Server-authoritative price for a product, including member pricing. */
export const getProductPricing = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ slug: SlugSchema, environment: EnvSchema.optional() }).parse(input))
  .handler(async ({ data, context }) => {
    const { publicClient } = await import("@/lib/shop.server");
    const supabase = publicClient();
    const { data: product } = await supabase
      .from("shop_products")
      .select(CATALOG_COLUMNS)
      .eq("slug", data.slug)
      .eq("active", true)
      .maybeSingle();
    if (!product) return { error: "Product not available" as const };
    const userId = (context as any)?.userId as string | undefined;
    const membership = userId
      ? await loadMembership((context as any).supabase, userId, data.environment)
      : { active: false, tier: null };
    return { price: resolvePrice(product as unknown as CatalogRow, membership) };
  });

export type DownloadFileGrant = {
  label: string;
  kind: string;
  format: string | null;
  sizeBytes: number | null;
  url: string;
  expiresInSeconds: number;
};

const DOWNLOAD_TTL_SECONDS = 15 * 60;

/**
 * Secure fulfillment. Ownership is re-derived server-side from the canonical
 * resolver on every request — the browser can never assert access, and the
 * private storage path never leaves the server.
 */
export const requestDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ slug: SlugSchema, environment: EnvSchema.optional() }).parse(input))
  .handler(async ({ data, context }): Promise<{ files: DownloadFileGrant[] } | { error: string }> => {
    const { userId } = context;

    const allowed = await checkRateLimit(`download:${userId}`, 30, 3600);
    if (!allowed) return { error: "Too many download requests. Please try again shortly." };

    const { bySlug, catalog } = await loadOwnership(context.supabase, userId, data.environment);
    const owned = bySlug[data.slug];
    if (!owned) return { error: "You do not own this product." };

    const product = catalog.find((row) => row.slug === data.slug) as
      | (CatalogRow & { download_asset_path?: string | null })
      | undefined;
    if (!product) return { error: "This product is no longer available." };
    if (!product.downloadable) {
      return { error: "This product is streaming-only and has no download." };
    }
    if (owned.source === "membership") {
      return { error: "Membership includes streaming access. Purchase the product to download it." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: assets, error: assetsErr } = await (supabaseAdmin.from("product_assets") as any)
      .select("kind, label, storage_path, format, size_bytes, sort_order, created_at")
      .eq("product_slug", data.slug)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (assetsErr) return { error: "Could not load your files. Please try again." };

    const rows: Array<{ kind: string; label: string; storage_path: string; format: string | null; size_bytes: number | null }> =
      assets && assets.length
        ? assets
        : product.download_asset_path
          ? [{ kind: "other", label: product.title, storage_path: product.download_asset_path, format: null, size_bytes: null }]
          : [];
    if (!rows.length) return { error: "No download is attached to this product yet." };

    const files: DownloadFileGrant[] = [];
    for (const row of rows) {
      const { data: signed, error: signErr } = await supabaseAdmin.storage
        .from("downloads")
        .createSignedUrl(row.storage_path, DOWNLOAD_TTL_SECONDS, { download: row.label });
      if (signErr || !signed) return { error: "Could not prepare your download. Please try again." };
      files.push({
        label: row.label,
        kind: row.kind,
        format: row.format,
        sizeBytes: row.size_bytes,
        url: signed.signedUrl,
        expiresInSeconds: DOWNLOAD_TTL_SECONDS,
      });
      await (supabaseAdmin.from("download_events") as any).insert({
        user_id: userId,
        product_slug: data.slug,
        order_id: owned.orderId,
        asset_label: row.label,
        storage_path: row.storage_path,
        status: "issued",
      });
    }
    return { files };
  });

export type StreamGrant = {
  label: string;
  kind: string;
  format: string | null;
  url: string;
  expiresInSeconds: number;
};

const STREAM_TTL_SECONDS = 60 * 60;

/**
 * Streaming fulfillment. Same canonical ownership check as downloads, but
 * membership-included access IS allowed to stream (it just cannot download),
 * and only audio/video assets are ever signed.
 */
export const requestStream = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ slug: SlugSchema, environment: EnvSchema.optional() }).parse(input))
  .handler(async ({ data, context }): Promise<{ tracks: StreamGrant[] } | { error: string }> => {
    const { userId } = context;

    const allowed = await checkRateLimit(`stream:${userId}`, 120, 3600);
    if (!allowed) return { error: "Too many stream requests. Please try again shortly." };

    const { bySlug, catalog } = await loadOwnership(context.supabase, userId, data.environment);
    const owned = bySlug[data.slug];
    if (!owned) return { error: "You do not have access to this product." };

    const product = catalog.find((row) => row.slug === data.slug) as CatalogRow | undefined;
    if (!product) return { error: "This product is no longer available." };
    if (!product.streamable) return { error: "This product is not available for streaming." };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: assets, error: assetsErr } = await (supabaseAdmin.from("product_assets") as any)
      .select("kind, label, storage_path, format, sort_order, created_at")
      .eq("product_slug", data.slug)
      .in("kind", ["audio", "video"])
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (assetsErr) return { error: "Could not load playback. Please try again." };
    if (!assets?.length) return { error: "No streaming media is attached to this product yet." };

    const tracks: StreamGrant[] = [];
    for (const row of assets as Array<{ kind: string; label: string; storage_path: string; format: string | null }>) {
      const { data: signed, error: signErr } = await supabaseAdmin.storage
        .from("downloads")
        .createSignedUrl(row.storage_path, STREAM_TTL_SECONDS);
      if (signErr || !signed) continue;
      tracks.push({
        label: row.label,
        kind: row.kind,
        format: row.format,
        url: signed.signedUrl,
        expiresInSeconds: STREAM_TTL_SECONDS,
      });
    }
    if (!tracks.length) return { error: "Could not prepare playback. Please try again." };
    return { tracks };
  });

/** Redeem an admin-created access code. Codes are never validated client-side. */
export const redeemAccessCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ code: z.string().trim().min(4).max(64) }).parse(input))
  .handler(async ({ data, context }): Promise<{ granted: string[] } | { error: string }> => {
    const { userId } = context;
    const allowed = await checkRateLimit(`access-code:${userId}`, 10, 3600);
    if (!allowed) return { error: "Too many attempts. Please try again later." };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const normalized = data.code.trim().toUpperCase();
    const { data: code } = await (supabaseAdmin.from("access_codes") as any)
      .select("id, product_slugs, max_redemptions, redemption_count, expires_at, active")
      .eq("code", normalized)
      .maybeSingle();
    if (!code || !code.active) return { error: "This code is not valid." };
    if (code.expires_at && new Date(code.expires_at).getTime() < Date.now()) return { error: "This code has expired." };
    if (code.redemption_count >= code.max_redemptions) return { error: "This code has already been fully redeemed." };

    const { error: redemptionErr } = await (supabaseAdmin.from("access_code_redemptions") as any).insert({
      code_id: code.id,
      user_id: userId,
    });
    // Unique violation = this user already redeemed it; no double grant, no error surface.
    if (redemptionErr && (redemptionErr as any).code !== "23505") {
      return { error: "Could not redeem this code." };
    }
    if (redemptionErr) return { error: "You have already redeemed this code." };

    await (supabaseAdmin.from("access_codes") as any)
      .update({ redemption_count: code.redemption_count + 1 })
      .eq("id", code.id);

    const granted: string[] = [];
    for (const slug of code.product_slugs ?? []) {
      const slugs = await grantEntitlements(supabaseAdmin, {
        userId,
        productSlug: slug,
        source: "code",
        reason: `Access code ${normalized}`,
      });
      granted.push(...slugs);
      await auditEntitlement(supabaseAdmin, {
        targetUserId: userId,
        productSlug: slug,
        action: "granted",
        reason: `Access code ${normalized}`,
        metadata: { code_id: code.id },
      });
    }
    return { granted };
  });

// ---------------- Admin commerce controls ----------------

async function assertAdminContext(context: any): Promise<void> {
  const { data, error } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (error || !data) throw new Error("Forbidden");
}

export const adminUpdateCatalogCommerce = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        slug: SlugSchema,
        active: z.boolean().optional(),
        productType: z.enum(["book", "album", "single", "activation", "course", "bundle", "merch", "other"]).optional(),
        downloadable: z.boolean().optional(),
        streamable: z.boolean().optional(),
        includedWithMembership: z.boolean().optional(),
        bundleItems: z.array(SlugSchema).max(50).optional(),
        entitlementSlugs: z.array(SlugSchema).max(50).optional(),
        memberAmountCents: z.number().int().min(0).max(10_000_00).nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdminContext(context);
    const patch: Record<string, unknown> = {};
    if (data.active !== undefined) patch.active = data.active;
    if (data.productType !== undefined) patch.product_type = data.productType;
    if (data.downloadable !== undefined) patch.downloadable = data.downloadable;
    if (data.streamable !== undefined) patch.streamable = data.streamable;
    if (data.includedWithMembership !== undefined) patch.included_with_membership = data.includedWithMembership;
    if (data.bundleItems !== undefined) patch.bundle_items = data.bundleItems;
    if (data.entitlementSlugs !== undefined) patch.entitlement_slugs = data.entitlementSlugs;
    if (data.memberAmountCents !== undefined) patch.member_amount_cents = data.memberAmountCents;
    if (!Object.keys(patch).length) return { ok: true };
    const { error } = await (context.supabase.from("shop_products") as any).update(patch).eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminGrantOwnership = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        userId: z.string().uuid(),
        slug: SlugSchema,
        reason: z.string().trim().min(3).max(500),
        expiresAt: z.string().datetime().nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdminContext(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const granted = await grantEntitlements(supabaseAdmin, {
      userId: data.userId,
      productSlug: data.slug,
      source: "admin",
      grantedBy: context.userId,
      reason: data.reason,
      expiresAt: data.expiresAt ?? null,
    });
    await auditEntitlement(supabaseAdmin, {
      actorId: context.userId,
      targetUserId: data.userId,
      productSlug: data.slug,
      action: "granted",
      reason: data.reason,
      metadata: { granted },
    });
    return { granted };
  });

export const adminRevokeOwnership = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        userId: z.string().uuid(),
        slug: SlugSchema,
        reason: z.string().trim().min(3).max(500),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdminContext(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Revoke only this product's grants (and children granted through it),
    // never unrelated purchases.
    await (supabaseAdmin.from("product_entitlements") as any)
      .update({ revoked_at: new Date().toISOString() })
      .eq("user_id", data.userId)
      .is("revoked_at", null)
      .or(`product_slug.eq.${data.slug},parent_product_slug.eq.${data.slug}`);
    await auditEntitlement(supabaseAdmin, {
      actorId: context.userId,
      targetUserId: data.userId,
      productSlug: data.slug,
      action: "revoked",
      reason: data.reason,
    });
    return { ok: true };
  });

export const adminCreateAccessCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        code: z.string().trim().min(4).max(64).regex(/^[A-Za-z0-9-]+$/),
        productSlugs: z.array(SlugSchema).min(1).max(20),
        maxRedemptions: z.number().int().min(1).max(10_000).default(1),
        expiresAt: z.string().datetime().nullable().optional(),
        note: z.string().trim().max(300).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdminContext(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await (supabaseAdmin.from("access_codes") as any).insert({
      code: data.code.toUpperCase(),
      product_slugs: data.productSlugs,
      max_redemptions: data.maxRedemptions,
      expires_at: data.expiresAt ?? null,
      note: data.note ?? null,
      created_by: context.userId,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminListCommerce = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdminContext(context);
    const [catalog, codes, audit] = await Promise.all([
      context.supabase.from("shop_products").select(CATALOG_COLUMNS).order("sort_order", { ascending: true }),
      context.supabase
        .from("access_codes")
        .select("id, code, product_slugs, max_redemptions, redemption_count, expires_at, active, note, created_at")
        .order("created_at", { ascending: false })
        .limit(100),
      context.supabase
        .from("entitlement_audit")
        .select("id, actor_id, target_user_id, product_slug, action, reason, created_at")
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    return {
      catalog: ((catalog.data ?? []) as any[]).map((row) => ({
        ...row,
        productType: normalizeProductType(row),
      })),
      codes: codes.data ?? [],
      audit: audit.data ?? [],
    };
  });
